import React, { useState, useEffect } from 'react';
import { apiGet } from '../api';
import {
  Users,
  UserCheck,
  Shield,
  UserCog,
  FileText,
  FileCheck,
  Radio,
  Satellite,
  Network,
  Globe,
  DollarSign,
  Banknote,
  Clock,
  CheckCircle,
  Hourglass,
  Gem,
  Coins,
  Box,
  Package,
  TrendingUp,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';

const StatIcons = {
  'Total Users': Users,
  'Active Users': UserCheck,
  'Total Moderators': Shield,
  'Active Moderators': UserCog,
  'Total License': FileText,
  'Active License': FileCheck,
  'Total Signal Channel': Radio,
  'Active Signal Channel': Satellite,
  'Total IPVPN': Network,
  'Active IPVPN': Globe,
  'Total Income': DollarSign,
  'Released Income': Banknote,
  'Pending Income': Clock,
  'Approved Withdrawl': CheckCircle,
  'Pending Withdrawl': Hourglass,
  'Available GUSD': Gem,
  'Available BFM': Coins,
  'Total Crypto Boxes': Box,
  'Opened Crypto Boxes': Package,
  'Total BFM in Crypto Box': TrendingUp,
};

const Dashboard = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet('/api/admin/dashboard/stats');
      if (response.data.success) {
        setStats(response.data.data);
      } else {
        setError('Failed to fetch dashboard stats');
      }
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatValue = (value) => {
    if (typeof value === 'number') {
      if (value >= 1000) {
        return value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }
      return value.toString();
    }
    return value;
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="space-y-1">
          <p className="text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/50">
            Overview
          </p>
          <div className="h-8 w-56 rounded bg-muted/30 animate-pulse" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="rounded-lg border border-border bg-card p-5 space-y-3 animate-pulse"
            >
              <div className="flex items-center justify-between">
                <div className="h-8 w-8 rounded-md bg-muted/40" />
              </div>
              <div className="h-6 w-20 rounded bg-muted/40" />
              <div className="h-4 w-28 rounded bg-muted/30" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center">
        <AlertTriangle className="h-10 w-10 text-destructive/70" />
        <div>
          <p className="text-base font-medium text-foreground">Failed to load dashboard</p>
          <p className="text-sm text-muted-foreground mt-1">{error}</p>
        </div>
        <button
          onClick={fetchDashboardStats}
          className="flex items-center gap-2 rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-foreground"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Overview
        </div>
        <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
          Dashboard <span className="text-gradient-ginox">Overview</span>
        </h1>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {stats.map((stat, index) => {
          const Icon = StatIcons[stat.permission] || TrendingUp;
          return (
            <div
              key={index}
              className="group relative rounded-lg border border-border bg-card px-5 py-4 transition-colors hover:border-border/80 hover:bg-card/80"
            >
              {/* Top row: icon + accent dot */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-white/[0.04] text-muted-foreground group-hover:text-foreground transition-colors">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="h-1.5 w-1.5 rounded-full bg-ginox-green/70 mt-1.5" />
              </div>

              {/* Value */}
              <p className="font-mono text-xl font-semibold text-foreground tabular-nums leading-none mb-1.5">
                {formatValue(stat.value)}
              </p>

              {/* Label */}
              <p className="text-xs text-muted-foreground truncate">{stat.permission}</p>

              {/* Bottom accent */}
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-ginox-green/0 via-ginox-green/20 to-ginox-green/0 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Dashboard;
